import { inject, Injector, runInInjectionContext } from '@angular/core';
import { CanActivateFn, CanMatchFn } from '@angular/router';
import { concatMap, from, isObservable, last, takeWhile } from 'rxjs';

// https://dev.to/dzinxed/a-new-way-of-ordering-guards-in-angular-24do

/**
 * Execute `CanActivateFn`s in order, waiting for the previous one to finish before starting the next one
 * @param guards guards to execute
 */
export function concatCanActivate(...guards: CanActivateFn[]): CanActivateFn {
    return (route, state) => {
        const injectionContext = inject(Injector);
        return from(guards).pipe(
            // for each guard, fire canActivate and wait for it to complete.
            concatMap((guard) => {
                return runInInjectionContext(injectionContext, () => {
                    const guardResult = guard(route, state);

                    if (isObservable(guardResult)) {
                        return guardResult;
                    }

                    return Promise.resolve(guardResult);
                });
            }),

            // don't execute the next guard if the current guard's result is not true and therefore routing shoudl be cancelled
            takeWhile((value) => value === true, true),

            // return the last guard's result
            last(),
        );
    };
}

/**
 * Execute `CanMatchFn`s in order, waiting for the previous one to finish before starting the next one
 * @param guards guards to execute
 */
export function concatCanMatch(...guards: CanMatchFn[]): CanMatchFn {
    return (route, state) => {
        const injectionContext = inject(Injector);
        return from(guards).pipe(
            // for each guard, fire canActivate and wait for it to complete.
            concatMap((guard) => {
                return runInInjectionContext(injectionContext, () => {
                    const guardResult = guard(route, state);

                    if (isObservable(guardResult)) {
                        return guardResult;
                    }

                    return Promise.resolve(guardResult);
                });
            }),

            // don't execute the next guard if the current guard's result is not true and therefore routing shoudl be cancelled
            takeWhile((value) => value === true, true),

            // return the last guard's result
            last(),
        );
    };
}
